import React, { useEffect, useState } from 'react';
import ButtonOpenStoreCounter from '../ButtonOpenStoreCounter';
import { METHOD_PAYMENT_TYPE_CARD_ONLINE, METHOD_PAYMENT_TYPE_GOOGLE_PAY, METHOD_PAYMENT_TYPE_PIX } from '../../../Utils/Constants/Checkout';
import { formatCartId, formatCurrency } from '../../../Utils/Formatters';
import { useLoginContext } from '../../../Contexts/LoginContext';
import { useCartContext } from '../../../Contexts/CartContext';
import Button from '../../Form/Button';
import { HiXMark } from 'react-icons/hi2';
import BoxContentQRCodeOrder from './BoxContentQRCodeOrder';
import OrderService from '../../../Services/OrderService';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext';
import { useTranslation } from 'react-i18next';
import { sanitizeText } from '../../../Utils/Helpers';
import CardPointsEarned from './CardPointsEarned';
import { useLayoutContext } from '../../../Contexts/LayoutContext';
import Cart from './Cart';

interface ProofPaymentProps {
    order: any;
    showButtonOpenNewCart: boolean;
}

const ProofPayment = ({
    order,
    showButtonOpenNewCart = true
}: ProofPaymentProps) => {

    const { t } = useTranslation();
    const { user } = useLoginContext();
    const { setShowCartIsFinished, cartFinished, setCartFinished } = useCartContext();
    const { orderType } = useOrderTypeContext();
    const { company } = useLayoutContext();

    const [ currentOrder, setCurrentOrder ] = useState(order)
    const [isValidated, setIsValidated] = useState(order.cart.cart_pdv_validation);
    const paidByCustomer = Boolean(
        order.payment_method?.type === METHOD_PAYMENT_TYPE_CARD_ONLINE
        || order.payment_method?.type === METHOD_PAYMENT_TYPE_GOOGLE_PAY
        || order.payment_method?.type === METHOD_PAYMENT_TYPE_PIX
    );

    useEffect(() => {
        const intervalRef = { current: null as NodeJS.Timeout | null };
        let isMounted = true;
        const controller = new AbortController();

        const getDetail = async () => {
            try {
                const orderService = new OrderService(orderType);
                const details = await orderService.detail(
                    { order_id: Number(order.id) },
                    { signal: controller.signal }
                );

                //* Se fechar o componente durante a requisição
                if (!isMounted) return;

                if (details) {
                    setCurrentOrder(details);

                    const hasValidated = details.cart.cart_pdv_validation;
                    setIsValidated(hasValidated);

                    if (hasValidated && cartFinished) {
                        setCartFinished((prev: any) => ({ ...prev, order: details }));
                    }

                    if (!hasValidated) {
                        if (!intervalRef.current) {
                            intervalRef.current = setInterval(() => {
                                // só executa se ainda estiver montado
                                if (!isMounted) return;
                                getDetail();
                            }, 5000);
                        }
                    } else {
                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                            intervalRef.current = null;
                        }
                    }
                }
            } catch (err: any) {
                if (err.name === "AbortError") {
                    return;
                }
            }
        };

        if (!isValidated) {
            getDetail();
        }

        return () => {
            // marca componente como desmontado
            isMounted = false;

            // ABORTA requisições pendentes
            controller.abort();

            // limpa interval se existir
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if(!order) return null;

    return (
        <section className='flex flex-col w-full gap-3 gap-0 pt-4_ h-full items-center justify-center text-center'>
            <div className='flex flex-col gap-4 w-full p-4 h-full items-center justify-center text-center'>

                <div className='flex gap-2 flex-col w-full items-center justify-center text-center -mt-2'>
                    <h3 className='text-xl font-extrabold text-shopfood-title px-2 flex flex-col gap-1'>
                        <span>{t('proof_payment.title', {user: user?.first_name})}</span>
                        <span>
                            {t('proof_payment.message', {
                                orderId: formatCartId(currentOrder.cart.display_id || currentOrder.cart.id),
                                tableName: currentOrder.cart?.table ? ` (${currentOrder.cart.table.name}) ` : ''
                            })}
                            {paidByCustomer ? t('proof_payment.paid_by_you') : t('proof_payment.paid_by_waiter')}.
                        </span>
                    </h3>

                    <div className="-rotate-6">
                        <div className={`text-[1.8rem] border-4 border-dashed my-1.5 uppercase px-4 font-extrabold zoomInIcon
                            ${isValidated ? 'border-gray-400 text-gray-400' : 'border-shopfood-success text-shopfood-success'}
                            `}>
                            {isValidated ? t('proof_payment.verified') : t('proof_payment.paid')}
                        </div>
                    </div>

                    <div className='flex flex-col leading-5'>
                        <span className='text-sm text-gray-400'>{t('cart.value_details.payment_methods')}</span>
                        {currentOrder.payment_methods?.map((method: any, index: number) =>
                        <div key={index} className='flex items-center gap-1 justify-center'>
                            <span className='text-lg font-extrabold text-shopfood-title'> { formatCurrency(method.pivot?.amount)}</span>
                            ({method?.name})
                        </div>
                        )}
                    </div>

                </div>

                <BoxContentQRCodeOrder
                    code={currentOrder.id}
                    show={true}
                    validated={isValidated}
                    hideQRCode={company?.config?.checkout?.show_qrcode === false}
                />

                {!isValidated && company?.config?.checkout?.show_qrcode !== false &&
                    <p className='font-semibold text-lg leading-none px-2 animate-fade-in text-shopfood-title' dangerouslySetInnerHTML={{ __html: sanitizeText(t('proof_payment.qrcode_message')) }} />
                }

                {!isValidated && company?.config?.checkout?.show_qrcode === false &&
                    <p className='font-medium text-lg leading-5 px-2 zoomInIconDelay' dangerouslySetInnerHTML={{ __html: sanitizeText(t('proof_payment.no_qrcode_message')) }} />
                }

                {/* CART */}
                <section className="
                    shadow-lg shadow-gray-100 rounded-xl w-full h-full text-left border border-dashed border-gray-300
                    transition-all ease-in-out duration-300 py-4 mt-2"
                    >
                    {currentOrder && <Cart getCart={currentOrder?.cart} onCloseCart={() => {}} onlyInfo />}
                </section>

                {currentOrder.points > 0 && (
                    <div className="pt-2">
                        <CardPointsEarned points={currentOrder.points} className="mx-auto" />
                    </div>
                )}
            </div>

            {showButtonOpenNewCart &&
                <div className='flex items-center justify-between gap-2 w-full sticky bottom-0 p-4 bg-white/65 backdrop-blur-sm'>
                    <ButtonOpenStoreCounter forceLabel proofPayment={true} label={t('proof_payment.button_open_new_comanda_label')} />
                    <Button rounded onClick={() => setShowCartIsFinished(false)}>
                        <div><HiXMark size={20} /></div>
                    </Button>
                </div>
            }
        </section>
    )
}

export default ProofPayment;